import { useState } from "react";
import { Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PhotoMasonry, Lightbox } from "@/components/site/PhotoMasonry";
import { SmartGallery } from "@/components/site/SmartGallery";

const GalleryBody = () => {
  const { content } = useContent();
  const g = content.photoGallery || {};
  const albums = (g.albums || []).filter((a) => (a.images || []).length > 0);
  // Lightbox works within a single album at a time
  const [box, setBox] = useState({ images: [], index: null });

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-32 md:pt-40 pb-24" data-testid="gallery-page">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal className="mb-14 max-w-2xl">
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-8"><ArrowLeft size={14} /> Accueil</Link>
            <div className="flex items-center gap-3 mb-5"><span className="h-px w-10 bg-electric" /><span className="text-xs tracking-[0.35em] uppercase text-electric">{g.eyebrow || "Galerie photo"}</span></div>
            <h1 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95] mb-6">{g.heading || "Les instants capturés."}</h1>
            {g.intro && <p className="text-base md:text-lg text-secondary2 font-light leading-relaxed">{g.intro}</p>}
          </Reveal>

          {albums.length === 0 ? (
            <p className="text-secondary2">Aucune photo pour le moment.</p>
          ) : (
            <div className="space-y-16">
              {albums.map((a, ai) => {
                const gs = a.gallerySettings;
                const slideshow = gs && gs.mode && gs.mode !== "fixed";
                return (
                  <section key={a.id || ai} data-testid={`album-${ai}`}>
                    {a.title && <div className="flex items-center gap-3 mb-6"><h2 className="font-serif text-2xl md:text-3xl text-white">{a.title}</h2><span className="h-px flex-1 bg-white/10" /><span className="text-xs text-secondary2">{a.images.length} photos</span></div>}
                    {slideshow
                      ? <SmartGallery images={a.images} options={gs} label="" />
                      : <PhotoMasonry images={a.images} onOpen={(idx) => setBox({ images: a.images, index: idx })} />}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <Lightbox images={box.images} index={box.index} onClose={() => setBox((b) => ({ ...b, index: null }))} />
    </div>
  );
};

export default function GalleryPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <Analytics />
        <GalleryBody />
      </ReactLenis>
    </ContentProvider>
  );
}
